import { Component } from '@theme/component';

class RespectoG980hFeatures extends Component {
  #controller;
  #drag;
  #opticsObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  #initialize() {
    this.#controller?.abort();
    this.#opticsObserver?.disconnect();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    for (const group of this.querySelectorAll('[data-g98-tabs]')) {
      const selected = group.querySelector('[role="tab"][aria-selected="true"]');
      this.#activate(group, Number(selected?.dataset.g98Tab || 0));
    }
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('input', this.#onInput, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerEnd, { signal });
    this.addEventListener('pointercancel', this.#onPointerEnd, { signal });
    this.addEventListener('dragstart', event => {
      if (event.target instanceof Element && event.target.closest('[data-g98-strip]')) event.preventDefault();
    }, { signal });
    this.classList.add('g98--ready');
    // Removing the attribute outside the viewport resets the CSS sequence.
    // Each return to the artwork starts a fresh light pass through the lenses.
    this.#opticsObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const active = entry.isIntersecting && entry.intersectionRatio >= .35;
        const previous = entry.target.hasAttribute('data-g98-lit');
        if (entry.target.matches('[data-g98-shield]')) {
          if (active === previous) continue;
          entry.target.removeAttribute('data-g98-lit');
          if (active) {
            for (const motion of entry.target.querySelectorAll('animateMotion')) {
              motion.replaceWith(motion.cloneNode(true));
            }
            // Commit the cleared CSS state before replaying the sequence.
            void entry.target.getBoundingClientRect();
            entry.target.setAttribute('data-g98-lit', '');
            for (const motion of entry.target.querySelectorAll('animateMotion')) motion.beginElement?.();
          }
          continue;
        }
        entry.target.toggleAttribute('data-g98-lit', active);
        for (const motion of entry.target.querySelectorAll('animateMotion')) {
          if (active && !previous) motion.beginElement?.();
          else if (!active && previous) motion.endElement?.();
        }
      }
    }, { threshold: [.35], rootMargin: '-80px 0px -8% 0px' });
    for (const art of this.querySelectorAll('[data-g98-optics], [data-g98-lock], [data-g98-shield]')) {
      art.removeAttribute('data-g98-lit');
      this.#opticsObserver.observe(art);
    }
  }

  #activate(group, selected, focus = false) {
    const tabs = [...group.querySelectorAll('[role="tab"]')];
    const index = Math.max(0, Math.min(tabs.length - 1, selected));
    for (const [i, tab] of tabs.entries()) {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    }
    for (const panel of group.querySelectorAll('[data-g98-panel]')) panel.hidden = Number(panel.dataset.g98Panel) !== index;
    for (const thumb of group.querySelectorAll('[data-g98-thumb]')) thumb.setAttribute('aria-pressed', String(Number(thumb.dataset.g98Thumb) === index));
    if (focus) tabs[index]?.focus({ preventScroll: true });
  }

  /** @param {MouseEvent} event */
  selectTab(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-g98-tab], [data-g98-thumb]');
    const group = button?.closest('[data-g98-tabs]');
    if (!button || !group || !this.contains(group)) return;
    this.#activate(group, Number(button.dataset.g98Tab ?? button.dataset.g98Thumb));
  }

  /** @param {MouseEvent} event */
  scrollRow(event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-g98-scroll]');
    const row = button?.closest('.g98-strip-wrap')?.querySelector('[data-g98-strip]');
    if (!(row instanceof HTMLElement)) return;
    row.scrollBy({ left: row.clientWidth * .8 * Number(button.dataset.g98Scroll), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }

  #onKeydown = event => {
    if (!(event.target instanceof Element)) return;
    const tab = event.target.closest('[role="tab"][data-g98-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const group = tab.closest('[data-g98-tabs]');
    const count = group.querySelectorAll('[role="tab"]').length;
    let index = Number(tab.dataset.g98Tab);
    if (event.key === 'ArrowRight') index = (index + 1) % count;
    if (event.key === 'ArrowLeft') index = (index + count - 1) % count;
    if (event.key === 'Home') index = 0;
    if (event.key === 'End') index = count - 1;
    event.preventDefault();
    this.#activate(group, index, true);
  };

  #setComparison(frame, value) {
    const position = Math.max(0, Math.min(100, value));
    frame.style.setProperty('--g98-position', `${position.toFixed(2)}%`);
    const input = frame.querySelector('input[type="range"]');
    if (input) input.value = String(Math.round(position));
  }

  #onInput = event => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const frame = event.target.closest('[data-g98-compare]');
    if (frame) this.#setComparison(frame, Number(event.target.value));
  };

  #onPointerDown = event => {
    if (!(event.target instanceof Element)) return;
    const row = event.target.closest('[data-g98-strip]');
    if (!row || event.pointerType !== 'mouse' || event.button !== 0) return;
    this.#drag = { row, pointerId: event.pointerId, x: event.clientX, start: row.scrollLeft, active: false };
  };

  #onPointerMove = event => {
    if (this.#drag && event.pointerId === this.#drag.pointerId) {
      const drag = this.#drag;
      const delta = event.clientX - drag.x;
      if (!drag.active && Math.abs(delta) > 6) {
        drag.active = true;
        drag.row.setPointerCapture(event.pointerId);
        drag.row.setAttribute('data-dragging', '');
      }
      if (drag.active) {
        event.preventDefault();
        drag.row.scrollLeft = drag.start - delta;
      }
      return;
    }
    if (!(event.target instanceof Element) || event.pointerType !== 'mouse' || event.buttons !== 0) return;
    const frame = event.target.closest('[data-g98-compare]');
    if (!frame || frame.contains(document.activeElement)) return;
    const rect = frame.getBoundingClientRect();
    if (rect.width) this.#setComparison(frame, (event.clientX - rect.left) / rect.width * 100);
  };

  #onPointerEnd = event => {
    const drag = this.#drag;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (drag.row.hasPointerCapture(event.pointerId)) drag.row.releasePointerCapture(event.pointerId);
    drag.row.removeAttribute('data-dragging');
    this.#drag = undefined;
  };

  disconnectedCallback() {
    this.#controller?.abort();
    this.#opticsObserver?.disconnect();
    if (this.#drag) this.#drag.row.removeAttribute('data-dragging');
    this.#drag = undefined;
    super.disconnectedCallback();
  }
}

if (!customElements.get('respecto-g980h-features')) customElements.define('respecto-g980h-features', RespectoG980hFeatures);
